import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Link as RouterLink } from 'react-router-dom';

import { useGetNAVQuery, useGetShareSubcategoriesQuery } from 'api/main';
import { EURO_SYMBOL } from 'utils/constants';
import { getShareSubcategoryIdFromIRI } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { CustomSelect, ListContainer, Loader } from 'components';
import {
    Box,
    Breadcrumbs,
    Link,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import * as icons from 'mdi-material-ui';
import NAVSubcategoryRow from './NAVSubcategoryRow';

const VLManagement = ({ fund }) => {
    const { data: navs = [], isFetching: dataLoading } = useGetNAVQuery({
        'shareSubcategory.shareCategory.investmentFund': fund?.id,
        pagination: false,
    });

    const { data: shareSubcategories = [], isLoading: subcategoriesLoader } =
        useGetShareSubcategoriesQuery({
            displayUnavailable: 1,
            'shareCategory.investmentFund': fund?.id,
        });

    const [selectedNAVDate, setSelectedNAVDate] = useState('');

    const mappedSubcategories = useMemo(
        () =>
            shareSubcategories.map(sc => ({
                ...sc,
                nav: selectedNAVDate
                    ? navs.find(
                          nav =>
                              getShareSubcategoryIdFromIRI(nav.shareSubcategory) === sc.id &&
                              moment(nav.date).format('L') === selectedNAVDate
                      )
                    : null,
            })),
        [navs, selectedNAVDate, shareSubcategories]
    );

    // Get all unique NAV dates for the select dropdown
    const allNAVDates = useMemo(() => {
        const seen = new Set();
        return navs
            .reduce((acc, nav) => {
                const date = moment(nav.date);
                const key = date.format('L');
                if (!seen.has(key)) {
                    seen.add(key);
                    acc.push(date);
                }
                return acc;
            }, [])
            .sort((a, b) => a - b);
    }, [navs]);

    useEffect(() => {
        if (allNAVDates.length) {
            const closestFutureDate = allNAVDates.find(date => moment(date).isAfter(moment()));
            setSelectedNAVDate(
                prevState =>
                    prevState || closestFutureDate?.format('L') || allNAVDates[0].format('L')
            );
        }
    }, [allNAVDates]);

    return (
        <ListContainer>
            <Breadcrumbs separator={<icons.MenuRight />} aria-label="breadcrumb">
                <Link
                    underline="hover"
                    href="/fund-management"
                    to="/fund-management"
                    component={RouterLink}
                >
                    {texts.FUND}
                </Link>
                <Link
                    underline="hover"
                    href={`/fund-management/${fund?.id}/attributes`}
                    to={`/fund-management/${fund?.id}/attributes`}
                    component={RouterLink}
                >
                    {texts.VL_MANAGEMENT} - {fund?.name}
                </Link>
            </Breadcrumbs>
            <Box marginY={3} textAlign="center">
                <Loader loading={subcategoriesLoader || dataLoading}>
                    <CustomSelect
                        value={selectedNAVDate}
                        onChange={e => setSelectedNAVDate(e.target.value)}
                    >
                        {allNAVDates.map(date => (
                            <MenuItem key={date.format('L')} value={date.format('L')}>
                                {date.format('L')}
                            </MenuItem>
                        ))}
                    </CustomSelect>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{texts.CATEGORIE}</TableCell>
                                    <TableCell>{texts.VL_DATE}</TableCell>
                                    <TableCell>VL ({EURO_SYMBOL})</TableCell>
                                    <TableCell>{texts.NAV_STATUS}</TableCell>
                                    <TableCell>{texts.SUBSCRIPTION_CUTOFF_DATE}</TableCell>
                                    <TableCell>{texts.SGP_CUTOFF_DATE}</TableCell>
                                    <TableCell>{texts.PUBLISHED_AT}</TableCell>
                                    <TableCell>{texts.PUBLISHED_BY}</TableCell>
                                    <TableCell>{texts.PUBLISH}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {mappedSubcategories
                                    .filter(sc => sc.nav) // only show subcategories with NAV for the selected date
                                    .map(sc => (
                                        <NAVSubcategoryRow
                                            key={sc.nav?.id || sc.id}
                                            subcategory={sc}
                                        />
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Loader>
            </Box>
        </ListContainer>
    );
};

VLManagement.propTypes = { fund: PropTypes.object };

export default VLManagement;
